import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import ItemRow from "../ItemRow";
import { relLabel } from "./dateUtils";

// The cross-date search-results list shown while the calendar search box has
// an active query. Each row gets a "Jump →" affordance to navigate the grid
// to that item's date.
export default function CalendarSearchResults({
    query, q, searchLoading, results, todayKey,
    onToggle, onDelete, onJump,
}) {
    return (
        <>
            <div className="flex items-center justify-between gap-2 mb-2">
                <p className="font-display text-xs uppercase tracking-widest text-warm-500" data-testid="cal-search-results-label">
                    {searchLoading ? "Searching…" : `${results.length} match${results.length === 1 ? "" : "es"} for "${query}"`}
                </p>
            </div>
            <AnimatePresence mode="wait">
                <motion.div
                    key={`results-${q}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="space-y-2.5"
                >
                    {!searchLoading && results.length === 0 && (
                        <div className="text-center py-6 px-4 bg-surface/50 rounded-2xl border border-dashed border-warm-300/40">
                            <p className="font-body text-sm text-warm-500">No items match &ldquo;{query}&rdquo;.</p>
                        </div>
                    )}
                    {results.map((r) => {
                        const kindLabel = r.kind === "list_item" ? "List" : r.kind.charAt(0).toUpperCase() + r.kind.slice(1);
                        const chipPrefix = r.kind === "habit" && r.date ? "starts "
                            : r.kind === "list_item" && r.date ? "for "
                            : "";
                        const dateChip = r.date ? `${chipPrefix}${relLabel(r.date, todayKey)}` : null;
                        const titleDisplay = r.kind === "list_item" && r.quantity
                            ? `${r.title} · ${r.quantity}`
                            : r.title;
                        return (
                            <div key={`${r.kind}-${r.id}`} className="flex items-stretch gap-2">
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] font-display uppercase tracking-widest text-warm-500 mb-1 px-1">{kindLabel}</p>
                                    <ItemRow
                                        title={titleDisplay}
                                        completed={r.completed}
                                        priority={r.priority}
                                        time={r.time}
                                        streak={r.streak || 0}
                                        dateChip={dateChip}
                                        onToggle={() => onToggle(r)}
                                        onDelete={() => onDelete(r)}
                                        testId={`cal-search-${r.kind}-${r.id}`}
                                    />
                                </div>
                                {r.date && (
                                    <button
                                        onClick={() => onJump(r.date)}
                                        className="self-end mb-1 shrink-0 text-[10px] font-display uppercase tracking-widest bg-cream hover:bg-cream/70 text-warm-800 px-2 py-1.5 rounded-full transition-all active:scale-95"
                                        data-testid={`cal-search-jump-${r.id}`}
                                        title="Jump to this date"
                                    >
                                        Jump →
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </motion.div>
            </AnimatePresence>
        </>
    );
}
